import { getJSON, RSAA } from 'redux-api-middleware';
import { AnyAction } from 'redux';
import { ClientViewModel } from 'stageclip-common';

import { normalizeClient } from '../normalizers/clientNormalizers';

import { ClientsActionTypes } from './clients.action-types';
import { NormalizedClientsUser } from './clients.types';
import * as EntityNormalizers from 'api/normalizers/entityNormalizers';
import * as UserNormalizers from 'api/normalizers/userNormalizers';
import { InternalActionTypes } from 'constants/actionTypes';

export interface FetchUserRequest {
  type: ClientsActionTypes.FETCH_CLIENT_USERS_REQUEST;
  payload: {
    id: string;
  };
}

export const fetchUsersRequest = (id: string): FetchUserRequest => ({
  type: ClientsActionTypes.FETCH_CLIENT_USERS_REQUEST,
  payload: {
    id,
  },
});

export interface FetchUserSuccess {
  type: ClientsActionTypes.FETCH_CLIENT_USERS_SUCCESS;
  payload: EntityNormalizers.NormalizedEntityArray<NormalizedClientsUser>;
}

export const fetchUsersSuccess = (
  normalizedClientsUsers: EntityNormalizers.NormalizedEntityArray<NormalizedClientsUser>,
): FetchUserSuccess => ({
  type: ClientsActionTypes.FETCH_CLIENT_USERS_SUCCESS,
  payload: normalizedClientsUsers,
});

export interface AddUserSuccess {
  type: ClientsActionTypes.ADD_CLIENT_USERS_SUCCESS;
  payload: EntityNormalizers.NormalizedSingleEntity<NormalizedClientsUser>;
}

export const addUserSuccess = (
  user: EntityNormalizers.NormalizedSingleEntity<NormalizedClientsUser>,
): AddUserSuccess => ({
  type: ClientsActionTypes.ADD_CLIENT_USERS_SUCCESS,
  payload: user,
});

export interface EditUserSuccess {
  type: ClientsActionTypes.EDIT_CLIENT_USER_SUCCESS;
  payload: EntityNormalizers.NormalizedSingleEntity<NormalizedClientsUser>;
}

export const editUserSuccess = (
  user: EntityNormalizers.NormalizedSingleEntity<NormalizedClientsUser>,
): EditUserSuccess => ({
  type: ClientsActionTypes.EDIT_CLIENT_USER_SUCCESS,
  payload: user,
});

export interface UserDeletedAction extends AnyAction {
  type: InternalActionTypes.UserDeleted;
  result: EntityNormalizers.NormalizedSingleEntity<UserNormalizers.NormalizedUsers>;
  meta: {
    userId: string;
  };
}

export type ClientActions = FetchUserRequest | FetchUserSuccess | AddUserSuccess | EditUserSuccess | UserDeletedAction;

// ------------------------------------RSAA-------------------------------

const FETCH_EMAIL_TEMPLATE_REQUEST = 'FETCH_EMAIL_TEMPLATE_REQUEST';
const FETCH_EMAIL_TEMPLATE_SUCCESS = 'FETCH_EMAIL_TEMPLATE_SUCCESS';
const FETCH_EMAIL_TEMPLATE_FAILURE = 'FETCH_EMAIL_TEMPLATE_FAILURE';

export const fetchEmailTemplate = (clientId: string) => ({
  [RSAA]: {
    types: [FETCH_EMAIL_TEMPLATE_REQUEST, FETCH_EMAIL_TEMPLATE_SUCCESS, FETCH_EMAIL_TEMPLATE_FAILURE],
    endpoint: `/clients/${clientId}/email-template`,
    method: 'GET',
  },
});

const FETCH_SURVEY_TEMPLATE_REQUEST = 'FETCH_SURVEY_TEMPLATE_REQUEST';
const FETCH_SURVEY_TEMPLATE_SUCCESS = 'FETCH_SURVEY_TEMPLATE_SUCCESS';
const FETCH_SURVEY_TEMPLATE_FAILURE = 'FETCH_SURVEY_TEMPLATE_FAILURE';

export const fetchSurveyTemplate = (clientId: string) => ({
  [RSAA]: {
    types: [FETCH_SURVEY_TEMPLATE_REQUEST, FETCH_SURVEY_TEMPLATE_SUCCESS, FETCH_SURVEY_TEMPLATE_FAILURE],
    endpoint: `/clients/${clientId}/survey-template`,
    method: 'GET',
  },
});

const UPDATE_CLIENT_REQUEST = 'UPDATE_CLIENT_REQUEST';
const UPDATE_CLIENT_SUCCESS = 'UPDATE_CLIENT_SUCCESS';
const UPDATE_CLIENT_FAILURE = 'UPDATE_CLIENT_FAILURE';

export const updateClient = (clientId: string, body: any) => ({
  [RSAA]: {
    types: [UPDATE_CLIENT_REQUEST, UPDATE_CLIENT_SUCCESS, UPDATE_CLIENT_FAILURE],
    endpoint: `/clients/${clientId}`,
    method: 'PATCH',
    body,
  },
});

const FETCH_NOTIFICATIONS_REQUEST = 'FETCH_NOTIFICATIONS_REQUEST';
const FETCH_NOTIFICATIONS_SUCCESS = 'FETCH_NOTIFICATIONS_SUCCESS';
const FETCH_NOTIFICATIONS_FAILURE = 'FETCH_NOTIFICATIONS_FAILURE';

export const fetchNotifications = () => ({
  [RSAA]: {
    types: [FETCH_NOTIFICATIONS_REQUEST, FETCH_NOTIFICATIONS_SUCCESS, FETCH_NOTIFICATIONS_FAILURE],
    endpoint: `/clients/notifications`,
    method: 'GET',
  },
});

const FETCH_CLIENT_REQUEST = 'FETCH_CLIENT_REQUEST';
export const FETCH_CLIENT_SUCCESS = 'FETCH_CLIENT_SUCCESS';
const FETCH_CLIENT_FAILURE = 'FETCH_CLIENT_FAILURE';

export const fetchClient = (id: string) => ({
  [RSAA]: {
    types: [
      FETCH_CLIENT_REQUEST,
      {
        type: FETCH_CLIENT_SUCCESS,
        payload: (action: any, state: any, res: any) =>
          // @ts-ignore
          getJSON(res).then((json: ClientViewModel) => normalizeClient(json)),
        meta: {
          unwrapPayload: true,
        },
      },
      FETCH_CLIENT_FAILURE,
    ],
    endpoint: `/clients/${id}`,
    method: 'GET',
  },
});

const DELETE_USER_REQUEST = 'DELETE_USER_REQUEST';
const DELETE_USER_SUCCESS = InternalActionTypes.UserDeleted as string;
const DELETE_USER_FAILURE = 'DELETE_USER_FAILURE';

export const createDeleteUser = (userId: string) => ({
  [RSAA]: {
    types: [
      DELETE_USER_REQUEST,
      {
        type: DELETE_USER_SUCCESS,
        meta: {
          userId,
        },
      },
      DELETE_USER_FAILURE,
    ],
    endpoint: `/users/${userId}`,
    method: 'DELETE',
  },
});

const ARCHIVE_CLIENT_REQUEST = 'ARCHIVE_CLIENT_REQUEST';
const ARCHIVE_CLIENT_FAILURE = 'ARCHIVE_CLIENT_FAILURE';

export const createArchiveClient = ({ id, ...body }: Partial<ClientViewModel>) => ({
  [RSAA]: {
    types: [
      ARCHIVE_CLIENT_REQUEST,
      {
        type: body.archived ? InternalActionTypes.ClientArchived : InternalActionTypes.ClientUnarchived,
        payload: (action: any, state: any, res: any) =>
          // @ts-ignore
          getJSON(res).then((json: any) => normalizeClient(json)),
        meta: {
          unwrapPayload: true,
        },
      },
      ARCHIVE_CLIENT_FAILURE,
    ],
    endpoint: `/clients/${id}`,
    method: 'PATCH',
    body,
  },
});

const FETCH_USER_ACTIONS_REQUEST = 'FETCH_USER_ACTIONS_REQUEST';
const FETCH_USER_ACTIONS_SUCCESS = 'FETCH_USER_ACTIONS_SUCCESS';
const FETCH_USER_ACTIONS_FAILURE = 'FETCH_USER_ACTIONS_FAILURE';

export const fetchUserActions = (id: string) => ({
  [RSAA]: {
    types: [FETCH_USER_ACTIONS_REQUEST, FETCH_USER_ACTIONS_SUCCESS, FETCH_USER_ACTIONS_FAILURE],
    endpoint: `/users/${id}/actions`,
    method: 'GET',
  },
});

const FETCH_CLIENT_ACTIONS_REQUEST = 'FETCH_CLIENT_ACTIONS_REQUEST';
const FETCH_CLIENT_ACTIONS_SUCCESS = 'FETCH_CLIENT_ACTIONS_SUCCESS';
const FETCH_CLIENT_ACTIONS_FAILURE = 'FETCH_CLIENT_ACTIONS_FAILURE';

export const fetchClientActions = (id: string) => ({
  [RSAA]: {
    types: [FETCH_CLIENT_ACTIONS_REQUEST, FETCH_CLIENT_ACTIONS_SUCCESS, FETCH_CLIENT_ACTIONS_FAILURE],
    endpoint: `/clients/${id}/actions`,
    method: 'GET',
  },
});
